import React from 'react';
import { Header } from '@tanstack/react-table';
import { DataTableColumnHeader } from './DataTableColumnHeader';
import { DataTableColumnMeta, DataTableHeaderConfig } from './types';
import { flexRender } from '@tanstack/react-table';

interface DataTableHeaderRendererProps<TData> {
  header: Header<TData, unknown>;
}

/**
 * Renders table header with support for three levels of complexity:
 * 1. Simple string - automatically wrapped in DataTableColumnHeader
 * 2. Header config object - wrapped with options
 * 3. Custom function/component - used as-is for full control
 */
export function DataTableHeaderRenderer<TData>({
  header,
}: DataTableHeaderRendererProps<TData>) {
  const columnDef = header.column.columnDef;
  const meta = columnDef.meta as DataTableColumnMeta | undefined;
  const headerMeta = meta?.header;

  // Case 1: Header is a string - automatically wrap in DataTableColumnHeader
  if (typeof columnDef.header === 'string') {
    return (
      <DataTableColumnHeader
        column={header.column}
        title={columnDef.header}
        canSort={headerMeta?.canSort ?? true}
        canHide={headerMeta?.canHide ?? true}
        infoSlot={headerMeta?.infoSlot}
      />
    );
  }

  // Case 2: Header is a config object - wrap with options
  if (
    columnDef.header &&
    typeof columnDef.header === 'object' &&
    !React.isValidElement(columnDef.header) &&
    'title' in columnDef.header
  ) {
    const headerConfig = columnDef.header as DataTableHeaderConfig;
    return (
      <DataTableColumnHeader
        column={header.column}
        title={headerConfig.title}
        canSort={headerConfig.canSort ?? headerMeta?.canSort ?? true}
        canHide={headerConfig.canHide ?? headerMeta?.canHide ?? true}
        infoSlot={headerConfig.infoSlot ?? headerMeta?.infoSlot}
      />
    );
  }

  // Case 3: Header is a function or custom component - use as-is (backward compatibility)
  return flexRender(columnDef.header, header.getContext());
}

